import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography,
  TextField, MenuItem, Button, IconButton, Alert, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  AITone, AILength, aiDraftReply, IAIDraftReplyResult,
} from "../../Api/emailTemplatesApi";

const NAVY = "#0F172A";
const CHAMPAGNE = "#C9A961";
const CHAMPAGNE_PALE = "#F5EFE0";
const HAIRLINE = "rgba(10,14,26,0.06)";

const TONES: { value: AITone; label: string }[] = [
  { value: "Professional",  label: "Professional" },
  { value: "Friendly",      label: "Friendly" },
  { value: "Luxury",        label: "Luxury / Premium" },
  { value: "Formal",        label: "Formal" },
  { value: "Warm",          label: "Warm" },
  { value: "Corporate",     label: "Corporate" },
  { value: "Sales-focused", label: "Sales-focused" },
];

const LENGTHS: { value: AILength; label: string }[] = [
  { value: "Short",    label: "Short" },
  { value: "Medium",   label: "Medium" },
  { value: "Detailed", label: "Detailed" },
];

const PURPOSE_PRESETS = [
  "Reply to inquiry",
  "Follow up on quote",
  "Send booking confirmation",
  "Payment reminder",
  "Event reminder",
  "Thank customer",
  "Re-engage cold lead",
];

interface Props {
  /** Optional thread context — Email Hub Reply passes this; Lead → Send leaves it null. */
  threadId?: number | null;
  /** Optional lead context — Lead → Send passes this; Email Hub leaves null
   *  unless the thread is linked (server resolves the link itself). */
  leadId?: number | null;
  /** Subject draft from the parent composer — narrows the AI's framing. */
  subject?: string;
  /** True when the body editor already has content — drives the
   *  "Replace existing body?" confirmation when applying. */
  hasExistingBody: boolean;
  onClose: () => void;
  /** Insert callback. The result includes a body with {{tokens}}
   *  already resolved against the lead/sender context, so the parent
   *  pastes it straight into the editor. */
  onApply: (result: IAIDraftReplyResult) => void;
}

/**
 * Shared "✨ AI Draft" modal used by both the Email Hub Reply modal
 * and the Lead → Send Email composer. Smart-context aware: when the
 * caller supplies a thread the AI reads the inbound transcript;
 * when a lead is in scope the AI also pulls the pipeline state.
 */
const AIReplyAssistant: React.FC<Props> = ({
  threadId, leadId, subject, hasExistingBody, onClose, onApply,
}) => {
  const [tone, setTone]       = useState<AITone>("Professional");
  const [length, setLength]   = useState<AILength>("Medium");
  const [purpose, setPurpose] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [result, setResult] = useState<IAIDraftReplyResult | null>(null);

  const onGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const out = await aiDraftReply({
        thread_id: threadId ?? null,
        lead_id:   leadId   ?? null,
        tone, length,
        purpose: purpose.trim() || undefined,
        subject: subject?.trim() || undefined,
      });
      setResult(out);
    } catch (e: any) {
      setError(e?.message || "Generation failed.");
    }
    setGenerating(false);
  };

  const onConfirmApply = () => {
    if (!result) return;
    if (hasExistingBody) {
      const ok = window.confirm("Replace the current body with the AI draft?");
      if (!ok) return;
    }
    onApply(result);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={1}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: CHAMPAGNE }} />
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: NAVY }}>
                AI Draft Reply
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                {threadId
                  ? "Reads the inbound transcript to match register and answer the customer's question."
                  : leadId
                  ? "Uses this lead's pipeline state + event details to personalize the draft."
                  : "Generates a polished draft you can edit before sending."}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Stack gap={1.5}>
          {subject && (
            <Box sx={{
              p: 1.2, borderRadius: 1.2,
              bgcolor: CHAMPAGNE_PALE, border: `1px solid ${HAIRLINE}`,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#A8893E", letterSpacing: "0.08em", mb: 0.3 }}>
                CURRENT SUBJECT
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: NAVY }} noWrap>
                {subject}
              </Typography>
            </Box>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} gap={1.2}>
            <TextField
              select size="small" fullWidth label="Tone"
              value={tone} onChange={(e) => setTone(e.target.value as AITone)}
            >
              {TONES.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" fullWidth label="Length"
              value={length} onChange={(e) => setLength(e.target.value as AILength)}
            >
              {LENGTHS.map(l => (
                <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            size="small" fullWidth multiline minRows={2}
            label="Purpose (optional)"
            placeholder="What should this reply accomplish?"
            value={purpose} onChange={(e) => setPurpose(e.target.value)}
          />

          <Box>
            <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
              Quick purposes:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
              {PURPOSE_PRESETS.map(p => (
                <Tooltip key={p} title="Use as purpose" arrow>
                  <Box
                    role="button"
                    onClick={() => setPurpose(p)}
                    sx={{
                      px: 1, py: 0.3, borderRadius: "999px",
                      bgcolor: "#fff", border: `1px solid ${HAIRLINE}`,
                      fontSize: 10.5, fontWeight: 600, color: "#475569",
                      cursor: "pointer",
                      "&:hover": { borderColor: CHAMPAGNE, bgcolor: CHAMPAGNE_PALE },
                    }}
                  >
                    {p}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>

          {error && <Alert severity="error" sx={{ fontWeight: 600 }}>{error}</Alert>}

          {result && (
            <Box sx={{
              border: `1px solid ${HAIRLINE}`, borderRadius: 1.2,
              bgcolor: "#fafbfc", p: 2,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em" }}>
                  DRAFT PREVIEW
                </Typography>
                {result.used_lead_id && (
                  <Typography sx={{ fontSize: 10.5, color: "#94a3b8" }}>
                    Personalized against lead #{result.used_lead_id}
                  </Typography>
                )}
              </Stack>
              <Box
                sx={{
                  fontSize: 13.5, color: "#1e293b", lineHeight: 1.55,
                  "& p": { mt: 0, mb: 1 },
                  maxHeight: 320, overflow: "auto",
                }}
                dangerouslySetInnerHTML={{ __html: result.body }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
        <Box>
          {result && (
            <Button onClick={onGenerate} disabled={generating} sx={{ textTransform: "none" }}>
              Regenerate
            </Button>
          )}
        </Box>
        <Stack direction="row" gap={1}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          {result ? (
            <Button
              onClick={onConfirmApply}
              variant="contained"
              sx={{
                textTransform: "none", fontWeight: 700,
                bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              Insert into Editor
            </Button>
          ) : (
            <Button
              onClick={onGenerate}
              disabled={generating}
              variant="contained"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontWeight: 700,
                bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              {generating ? "Drafting…" : "Generate Draft"}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AIReplyAssistant;
